/*
 *  Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Command } from '../../../model/Command';
import { ActionRequest } from '../../../model/ActionRequest';
import { Filter } from '../../../model/Filter';
import { BaseController } from '../../BaseController';
import { CloseViewController } from './CloseView';
import { CurativeActionViewController } from './CurativeActionView';

class ActionRequestViewController extends BaseController {
    /** @ngInject */
    constructor(configuration, actionRequestService, curativeActionService, actionRequestServiceLevelService, actionRequestStateBeforeService, actionRequestStateAfterService, actionRequestSymptomService, equipmentService, equipmentTemplateService, alarmService,
        companyService, accountService, waitSpinnerService, $q, $state, $stateParams, $log, notificationService, $scope,
        dialogService, authenticationService, originService, filterService) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.actionRequestService = actionRequestService;
        this.curativeActionService = curativeActionService;
        this.actionRequestServiceLevelService = actionRequestServiceLevelService;
        this.actionRequestStateBeforeService = actionRequestStateBeforeService;
        this.actionRequestStateAfterService = actionRequestStateAfterService;
        this.actionRequestSymptomService = actionRequestSymptomService;
        this.equipmentService = equipmentService;
        this.equipmentTemplateService = equipmentTemplateService;
        this.alarmService = alarmService;
        this.companyService = companyService;
        this.accountService = accountService;
        this.authenticationService = authenticationService;
        this.originService = originService;
        this.filterService = filterService;
        this.dialogService = dialogService;
        this.$log.debug('Controller \'ActionRequestView\' loaded');
    }

    $doCheck() {
        if (this.actionRequest) {
            if (!angular.equals(this.actionRequest, this.pristineEntity)) {
                this.viewHeaderDisabled.cancelDisabled = true;
                this.viewHeaderDisabled.completeDisabled = true;
                this.viewHeaderDisabled.curativeDisabled = true;
            } else {
                this.viewHeaderDisabled.curativeDisabled = false;
                if (this.actionRequestView.$valid) {
                    this.viewHeaderDisabled.cancelDisabled = false;
                    this.viewHeaderDisabled.completeDisabled = false;
                } else {
                    this.viewHeaderDisabled.cancelDisabled = true;
                    this.viewHeaderDisabled.completeDisabled = true;
                }
            }
        }
    }

    $onInit() {
        super.$onInit();

        this.waitSpinnerService.visible = true;
        this.formDisabled = false;
        this.accountsDisabled = false;
        this.alarmsDisabled = false;
        this.alarms = [];
        this.selectedAlarms = [];
        this.commands = [];
        this.viewHeaderDisabled = [];
        this.actionRequestView = [];
        this.edited = {};
        this.oldCommentValue = {};

        // The updateMode flag can be used to know the context in which this controller is running: creation or update.
        let initPromise;
        let equipmentsPromise;
        let companiesPromise;
        if (this.authenticationService.userHasRole(['ADMIN', 'OPERATION_MANAGER'])) {
            companiesPromise = this.companyService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'}),
                data: this.filterService.buildFilters([Filter.equalFilter('enabled', true)])
            });
        } else {
            companiesPromise = [];
        }
        let selectedEquipmentPromise;
        let alarmsPromise;
        let accountsPromise;
        let serviceLevelsPromise;
        if (this.$stateParams.hasOwnProperty('id')) {
            this.updateMode = true;
            this.title = 'ui.actionrequest.update';
            initPromise = this.actionRequestService.getEntity(this.$stateParams.id);
            equipmentsPromise = [];
            alarmsPromise = [];
            if (this.authenticationService.userHasRole(['ADMIN', 'OPERATION_MANAGER', 'ACTION_REQUEST_COMPLETE'])) {
                accountsPromise = initPromise.then(entity => this.getAccounts(entity.delegateeCompanyId));
            } else {
                accountsPromise = [];
            }
        } else {
            this.updateMode = false;
            this.title = 'ui.actionrequest.creation';
            initPromise = this.$q.resolve(false);
            equipmentsPromise = this.equipmentService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'})
            });
            if (this.$stateParams.equipmentId) {
                // If equipment is preselected, the list of accounts shall be preloaded and contain accounts in the
                // delegatee company of the equipment.
                selectedEquipmentPromise = this.equipmentService.getEntity(this.$stateParams.equipmentId);
                accountsPromise = selectedEquipmentPromise.then(
                    equipment => this.getAccounts(equipment.delegateeCompanyId)
                );
                // If equipment is preselected, list of alarms shall be preloaded with the alarms related to this
                // equipment.
                alarmsPromise = this.getAlarms(this.$stateParams.equipmentId);
            } else {
                accountsPromise = [];
                alarmsPromise = [];
            }
        }
        if (this.authenticationService.userHasRole(['ADMIN', 'OPERATION_MANAGER', 'ACTION_REQUEST_COMPLETE'])) {
            serviceLevelsPromise = this.actionRequestServiceLevelService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'})
            });
        } else {
            serviceLevelsPromise = [];
        }

        this.handleRestServiceCall(this.$q.all({
            entity: initPromise,
            equipments: equipmentsPromise,
            companies: companiesPromise,
            accounts: accountsPromise,
            selectedEquipment: selectedEquipmentPromise,
            alarms: alarmsPromise,
            serviceLevels: serviceLevelsPromise
        }), data => {
            this.pristineEntity = new ActionRequest(data.entity);
            this.equipments = data.equipments;
            this.companies = data.companies;
            this.accounts = data.accounts;
            this.serviceLevels = data.serviceLevels;

            if (data.selectedEquipment) {
                this.selectedEquipment = data.selectedEquipment;
                this.pristineEntity.equipmentId = data.selectedEquipment.id;
                this.pristineEntity.delegateeCompanyId = data.selectedEquipment.delegateeCompanyId;
            }
            this.initDomainDependentList(this.pristineEntity.domainId);

            this.reset();
            this.loadAlarms(data.alarms);
            this.initCommands();
            this.initDefaultValues();
        }
        ).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    /**
     * Init the stateBefore and statAfter lists according to the domainId
     * @param domainId
     */
    initDomainDependentList(domainId) {
        let statesBeforePromise;
        let statesAfterPromise;
        let symptomsPromise;
        if (domainId) {
            statesBeforePromise = this.actionRequestStateBeforeService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'}),
                data: this.filterService.buildFilters([Filter.equalFilter('domain', domainId)])
            });
            statesAfterPromise = this.actionRequestStateAfterService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'}),
                data: this.filterService.buildFilters([Filter.equalFilter('domain', domainId)])
            });
            symptomsPromise = this.actionRequestSymptomService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'}),
                data: this.filterService.buildFilters([Filter.equalFilter('domain', domainId)])
            });
        } else {
            statesBeforePromise = [];
            statesAfterPromise = [];
            symptomsPromise = [];
        }

        this.handleRestServiceCall(this.$q.all({
            stateBefores: statesBeforePromise,
            stateAfters: statesAfterPromise,
            symptoms : symptomsPromise }), data => {
            this.stateBefores = data.stateBefores;
            this.stateAfters = data.stateAfters;
            this.symptoms = data.symptoms;
        });
    }

    /**
     * Initializes the commands available in the view header.
     */
    initCommands() {
        if (this.updateMode) {
            switch (this.actionRequest.status) {
            case 'OPENED':
                this.commands.push(
                    Command.handlerCommand('ui.actionrequest.addaction', 'images/svg/add.svg',
                        () => this.showCurativeActionViewDialog(), ['ADMIN', 'CURATIVE_ACTION_CREATE'], "curativeDisabled"),
                    Command.handlerCommand('ui.actionrequest.complete', 'images/svg/flag.svg',
                        () => this.showStatusUpdateConfirmationDialog('COMPLETED'),
                        ['ADMIN', 'ACTION_REQUEST_COMPLETE'], "completeDisabled"),
                    Command.handlerCommand('ui.actionrequest.cancel', 'images/svg/cancel.svg',
                        () => this.showStatusUpdateConfirmationDialog('CANCELED'), ['ADMIN', 'ACTION_REQUEST_WRITE']
                        ,  "cancelDisabled")
                );
                break;
            case 'COMPLETED':
                this.commands.push(
                    Command.handlerCommand('ui.actionrequest.reopen', 'images/svg/wrench.svg',
                        () => this.showStatusUpdateConfirmationDialog('OPENED'), ['ADMIN', 'ACTION_REQUEST_WRITE']),
                    Command.handlerCommand('ui.actionrequest.close', 'images/svg/checked.svg',
                        () => this.showCloseViewDialog(), ['ADMIN', 'ACTION_REQUEST_WRITE']),
                    Command.handlerCommand('ui.actionrequest.cancel', 'images/svg/cancel.svg',
                        () => this.showStatusUpdateConfirmationDialog('CANCELED'), ['ADMIN', 'ACTION_REQUEST_WRITE'])
                );
                break;
            default:
                break;
            }
        }
    }

    /**
     * Initializes default values in the form.
     */
    initDefaultValues() {
        if (this.actionRequest.serviceLevelId === null) {
            this.actionRequest.serviceLevelId = 0;  //Optimal
        }
        if (!this.actionRequest.activeOnDuty) {
            this.actionRequest.activeOnDuty = false
        }
        if (!this.actionRequest.activeStat) {
            this.actionRequest.activeStat = false
        }
    }

    /**
     * Loads alarms pending on an equipment.
     *
     * @param {Number} [equipmentId] Equipment ID.
     */
    loadDelegateeCompanyAndAlarms() {
        if (this.selectedEquipment && !this.updateMode) {
            this.actionRequest.equipmentId = this.selectedEquipment.id;
            this.actionRequest.delegateeCompanyId = this.selectedEquipment.delegateeCompanyId;
            let accountsPromise;
            if (this.actionRequest.delegateeCompanyId) {
                accountsPromise = this.getAccounts(this.actionRequest.delegateeCompanyId);
            }  else {
                accountsPromise = [];
            }
            this.accountsDisabled = true;
            this.alarmsDisabled = true;
            this.handleRestServiceCall(this.$q.all({
                accounts: accountsPromise,
                alarms: this.getAlarms(this.actionRequest.equipmentId)
            }), data => {
                this.accounts = data.accounts;
                this.loadAlarms(data.alarms);
            }).catch(() => {
                this.notificationService.pushMessage('ui.actionrequest.alarmloadingfailed',
                    this.configuration.NOTIFIER_LEVEL_ERROR);
            }).finally(() => {
                this.accountsDisabled = false;
                this.alarmsDisabled = false;
            });
        }
    }

    /**
     * Loads accounts in a company.
     *
     * @param {Number} [companyId] Company ID.
     */
    loadAccounts(companyId) {
        if (companyId) {
            this.accountsDisabled = true;
            this.handleRestServiceCall(
                this.getAccounts(companyId),
                accounts => {this.accounts = accounts;}
            ).catch(() => {
                this.notificationService.pushMessage('ui.actionrequest.userloadingfailed',
                    this.configuration.NOTIFIER_LEVEL_ERROR);
            }).finally(() => {
                this.accountsDisabled = false;
            });
        }
    }

    /**
     * Gets accounts in a company.
     *
     * @param {Number} companyId Company ID.
     * @return {Promise} Promise containing the accounts loaded.
     */
    getAccounts(companyId) {
        return this.accountService.findEntities({
            data: this.filterService.buildFilters([
                Filter.equalFilter('enabled', true),
                Filter.equalFilter('system', false),
                Filter.equalFilter('company.id', companyId)
            ])
        });
    }

    /**
     * Gets pending alarms for an equipment, and not handled in an action request.
     *
     * @param {Number} equipmentId Equipment ID.
     * @return {Promise} Promise containing the alarms loaded.
     */
    getAlarms(equipmentId) {
        return this.alarmService.findEntities({
            data: this.filterService.buildFilters([
                Filter.alarmStatusFilter('PENDING'),
                Filter.equalFilter('equipment.id', equipmentId),
                Filter.nullFilter('actionRequest')
            ])
        });
    }

    loadAlarms(alarms) {
        if (!this.updateMode) {
            this.alarms.splice(0);
            this.selectedAlarms = [];
            alarms.forEach(alarm => {
                this.alarms.push(alarm);
                this.selectedAlarms.push(alarm.id);
            });
            this.actionRequest.setAlarms(this.selectedAlarms);
        }
    }

    /**
     * Shows a dialog containing the form to add a curative action. When the dialog is closed, the action request
     * view is refreshed.
     *
     * @return {Promise} A promise of the action.
     */
    showCurativeActionViewDialog() {
        this.handleRestServiceCall(
            this.equipmentService.getEntity(this.actionRequest.equipmentId).then(
                equipment => this.equipmentTemplateService.getEntity(equipment.equipmentTemplateId)
            ), equipmentTemplate => {
            this.dialogService.showDialog(
                    CurativeActionViewController,
                    'actionrequest/component/curativeaction-view.html', {
                        actionRequestId: this.actionRequest.id,
                        parts: equipmentTemplate.parts
                    }
                ).then(confirmed => {
                    if (confirmed) {
                        this.$state.reload();
                    }
                });
        }
        );
    }

    /**
     * Shows a dialog containing a form to close the action request. When the dialog is closed, the action request view
     * is refreshed.
     *
     * @return {Promise} A promise of the action.
     */
    showCloseViewDialog() {
        this.handleRestServiceCall(
            this.originService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'code'}),
                data: this.filterService.buildFilters([
                    Filter.equalFilter('domain.id', this.actionRequest.domainId, 'model.domain.domain')
                ])
            }), origins => {
            this.dialogService.showDialog(
                 CloseViewController,
                'actionrequest/component/close-view.html',
                { origins: origins }
            ).then(causeId => {
                if (causeId) {
                    this.updateStatus('CLOSED', {causeId});
                }
            });
        }
        );
    }

    /**
     * Shows a confirmation dialog before changing the status. If the dialog is confirmed, the action request' status is
     * updated.
     *
     * @param {status} New status.
     * @return {Promise} A promise of the action.
     */
    showStatusUpdateConfirmationDialog(status) {
        let messageKey;
        switch (status) {
        case 'CANCELED':
            messageKey = 'ui.actionrequest.cancelconfirmation';
            break;
        case 'OPENED':
            messageKey = 'ui.actionrequest.reopenconfirmation';
            break;
        case 'COMPLETED':
            messageKey = 'ui.actionrequest.completeconfirmation';
            break;
        default:
            break;
        }

        let promise;
        if (messageKey && this.updateMode) {
            promise = this.dialogService.showConfirmationDialogWithMessage(messageKey).then(confirmed => {
                if (confirmed) {
                    this.updateStatus(status);
                }
            });
        } else {
            promise = this.$q.reject(false);
        }
        return promise;
    }

    save() {
        this.formDisabled = true;
        let serviceActionPromise;
        if (this.updateMode) {
            serviceActionPromise = this.actionRequestService.updateEntity(this.actionRequest);
        } else {
            serviceActionPromise = this.actionRequestService.createEntity(this.actionRequest);
        }
        this.handleRestServiceCall(serviceActionPromise, () => {
            const message = 'ui.actionrequest.' + (this.updateMode ? 'updated' : 'created');
            this.notificationService.pushMessage(message, this.configuration.NOTIFIER_LEVEL_INFO);
            if (this.updateMode) {
                this.$state.reload();
            } else {
                this.$state.go('gmao.actionrequest.list');
            }
        }).finally(() => {
            this.formDisabled = false;
        });
    }

    updateStatus(status, data) {
        if (this.updateMode) {
            this.formDisabled = true;
            let serviceActionPromise;
            switch (status) {
            case 'OPENED':
                serviceActionPromise = this.actionRequestService.reopen(this.actionRequest.id);
                break;
            case 'COMPLETED':
                serviceActionPromise = this.actionRequestService.complete(this.actionRequest.id);
                break;
            case 'CLOSED':
                serviceActionPromise = this.actionRequestService.close(this.actionRequest.id, data);
                break;
            case 'CANCELED':
                serviceActionPromise = this.actionRequestService.cancel(this.actionRequest.id);
                break;
            default:
                serviceActionPromise = this.$q.resolve(true);
                break;
            }

            this.handleRestServiceCall(serviceActionPromise, () => {
                this.notificationService.pushMessage('ui.actionrequest.updated',
                    this.configuration.NOTIFIER_LEVEL_INFO);
                if ('CLOSED' === status) {
                    this.$state.go('gmao.actionrequest.list');
                } else {
                    this.$state.reload();
                }
            }).finally(() => {
                this.formDisabled = false;
            });
        }
    }

    /**
     * Restores the initial state of the view, and the map.
     */
    reset() {
        this.actionRequest = angular.copy(this.pristineEntity);
    }

    /**
     * Start edition of a comment field
     */
    startEdit(index, oldValue) {
        this.edited[index] = true;
        this.oldCommentValue[index] = oldValue;
    }

    /**
     * Commit comment modification
     */
    commitComment(index) {
        let currativeAction = this.actionRequest.revisions[index];
        if (currativeAction.comment === this.oldCommentValue[index].comment) {
            this.edited[index] = false;
        } else {
            this.curativeActionService.patchEntity(
                {id: currativeAction.id, actionRequestId: this.actionRequest.id},
                {'comment': currativeAction.comment})
                .then(result => {
                    currativeAction = result;
                    this.edited[index] = false;
                })
        }
    }

    /**
     * Rollback comment change
     */
    rollbackComment(index) {
        this.actionRequest.revisions[index].comment = this.oldCommentValue[index];
        this.edited[index] = false;
    }
}

export const actionRequestViewComponent = {
    template: require('./actionrequest-view.html'),
    controller: ActionRequestViewController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/actionrequest/component/ActionRequestView.js